<template>
  <div
    class="coupon-card-wrapper"
    :class="{ 'is-selected': isSelected, 'is-locked': isLocked, 'is-browse': isBrowseMode, 'is-cart': isCartMode, 'is-history': isHistoryItem }"
    @click="handleCardClick"
  >
    <div class="visual-wrapper" :class="visualClass">
      <img :src="couponImage" alt="coupon-image" class="coupon-image">
      <div class="visual-overlay" />
    </div>
    <div class="content-wrapper">
      <div v-if="isCartMode" class="content-top content-top-cart">
        <p class="header header-cart">
          {{ displayName }}
        </p>
      </div>
      <template v-else>
      <div class="content-top">
        <button
          v-if="isBrowseMode"
          class="detail-link detail-link-top"
          type="button"
          @click.stop="handleCardClick"
        >
          {{ t('myCoupon.card.viewDetails') }}
        </button>
        <div class="chip-row">
          <p class="badge content-badge" :class="badgeClass">
            {{ badgeLabel }}
          </p>
          <p class="state-chip" :class="stateChipClass">
            {{ stateLabel }}
          </p>
        </div>
        <p class="header">
          {{ displayName }}
        </p>
        <p class="subtitle">
          {{ subtitleLabel }}
        </p>
        <div class="meta-row">
          <p class="meta-pill is-days-left" v-if="recentLinkDaysLeftLabel">
            {{ recentLinkDaysLeftLabel }}
          </p>
          <p class="meta-pill" v-if="scopeLabel">
            {{ scopeLabel }}
          </p>
          <p class="meta-pill" v-if="minLabel">
            {{ minLabel }}
          </p>
          <p class="meta-pill" v-if="usageLimitLabel">
            {{ usageLimitLabel }}
          </p>
        </div>
        <p class="detail" v-if="detailLabel">
          {{ detailLabel }}
        </p>
        <p class="lock-reason" v-if="isLocked && coupon.lock_reason">
          {{ coupon.lock_reason }}
        </p>
        <p class="clinic-code" v-if="clinicBenefitCodeLabel">
          {{ clinicBenefitCodeLabel }}
        </p>
      </div>
      <div class="content-bottom">
        <p class="expired" v-if="displayExpired">
          {{ t('myCoupon.card.expired') }} {{ displayExpired }}
        </p>
        <p class="expired" v-else-if="historyTimestampLabel">
          {{ historyTimestampLabel }}
        </p>
        <p class="expired" v-else>
          {{ t('myCoupon.card.noExpired') }}
        </p>
      </div>
      </template>
    </div>
    <div
      class="check-wrapper"
      v-if="showCheck"
      :class="{ 'is-selected': isSelected }"
    >
      <img src="@/assets/icons/small-check.svg">
    </div>
    <button
      v-if="showActionButton"
      class="action-btn"
      :class="{
        locked: isLocked,
        primary: isPointsRedeemable,
        free: isFreeRedeemable,
        secondary: isOwnedOrPublic
      }"
      :disabled="isLocked"
      @click.stop="handleAction"
    >
      {{ actionLabel }}
    </button>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

export default {
  emits: ['action', 'detail'],
  props: {
    coupon: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'cart',
    },
    noSelection: {
      type: Boolean,
      default: false,
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n()

    /** computed */
    const isHistoryItem = computed(() => props.coupon?.display_state === 'history')
    const displayExpired = computed(() => {
      if (isHistoryItem.value) {
        return null
      }
      const effectiveExpired = props.coupon?.owned_expires_at || props.coupon?.expired
      return effectiveExpired ? dayjs(effectiveExpired).format('DD/MM/YYYY') : null
    })
    const displayName = computed(() => {
      if (props.coupon?.display_name) {
        return props.coupon.display_name
      }
      return `${t('myCoupon.card.discount')} ${props.coupon.discount_amount}${props.coupon.discount_type === 'amount' ? '฿' : '%'}`
    })
    const couponImage = computed(() => (
      props.coupon?.picture_full || props.coupon?.picture_url || props.coupon?.picture_thumbnail
    ))
    const detailLabel = computed(() => props.coupon?.display_detail || '')
    const isSelected = computed(() => props.mode === 'cart' && props.coupon?.isSelected)
    const isBrowseMode = computed(() => props.mode === 'browse')
    const isCartMode = computed(() => props.mode === 'cart')
    const isLocked = computed(() => props.coupon?.display_state === 'locked')
    const isRedeemable = computed(() => props.coupon?.display_state === 'redeemable')
    const isFreeRedeemable = computed(() => isRedeemable.value && Number(props.coupon?.burn_coin || 0) === 0)
    const isPointsRedeemable = computed(() => isRedeemable.value && Number(props.coupon?.burn_coin || 0) > 0)
    const isOwnedOrPublic = computed(() => ['owned', 'public'].includes(props.coupon?.display_state))
    const isClinicBenefit = computed(() => props.coupon?.benefit_type === 'clinic_benefit')
    const isOwnedClinicBenefit = computed(() => isClinicBenefit.value && props.coupon?.display_state === 'owned')
    const showCheck = computed(() => !props.noSelection && props.mode === 'cart')
    const showActionButton = computed(() => isBrowseMode.value && !isHistoryItem.value)
    const badgeLabel = computed(() => {
      if (isClinicBenefit.value) {
        return t('myCoupon.card.badges.inStore')
      }
      if (props.coupon?.is_birthday_month_only) {
        return t('myCoupon.card.badges.birthday')
      }
      if (Number(props.coupon?.burn_coin || 0) > 0) {
        return t('myCoupon.card.badges.points')
      }
      return t('myCoupon.card.badges.coupon')
    })
    const badgeClass = computed(() => ({
      'is-in-store': isClinicBenefit.value,
      'is-birthday': props.coupon?.is_birthday_month_only,
      'is-points': Number(props.coupon?.burn_coin || 0) > 0 && !isClinicBenefit.value,
    }))
    const scopeLabel = computed(() => {
      if (props.coupon.is_birthday_month_only) {
        return t('myCoupon.card.birthdayOnly')
      }
      if (props.coupon.product_scope === 'mila_claire_only') {
        return t('myCoupon.card.milaClaireOnly')
      }
      if (props.coupon.product_scope === 'exclude_mila_claire') {
        return t('myCoupon.card.makalinOnly')
      }
      return ''
    })
    const minLabel = computed(() => (
      props.coupon.min_total > 0
        ? `${t('myCoupon.card.min')} ${props.coupon.min_total}฿`
        : ''
    ))
    const stateLabel = computed(() => {
      if (isOwnedClinicBenefit.value) {
        return t('myCoupon.card.clinicBenefitReady')
      }
      if (props.coupon?.display_state === 'owned') {
        return t('myCoupon.card.owned')
      }
      if (props.coupon?.display_state === 'history') {
        return props.coupon?.history_type === 'used'
          ? t('myCoupon.card.used')
          : t('myCoupon.card.expiredLabel')
      }
      if (props.coupon?.display_state === 'public') {
        return t('myCoupon.card.available')
      }
      if (props.coupon?.display_state === 'redeemable') {
        return isFreeRedeemable.value
          ? t('myCoupon.card.freeRedeemable')
          : t('myCoupon.card.pointsRedeemable')
      }
      if (props.coupon?.display_state === 'locked') {
        return t('myCoupon.card.locked')
      }
      return ''
    })
    const stateChipClass = computed(() => ({
      'is-locked': isLocked.value,
      'is-free': isFreeRedeemable.value,
      'is-points': isPointsRedeemable.value,
      'is-owned': props.coupon?.display_state === 'owned',
      'is-history': isHistoryItem.value,
      'is-public': props.coupon?.display_state === 'public',
      'is-clinic': isOwnedClinicBenefit.value,
    }))
    const subtitleLabel = computed(() => {
      if (isOwnedClinicBenefit.value) {
        return t('myCoupon.card.clinicRewardSubtitle')
      }
      if (isFreeRedeemable.value) {
        return t('myCoupon.card.freeRewardSubtitle')
      }
      if (isPointsRedeemable.value) {
        return t('myCoupon.card.pointsRewardSubtitle')
      }
      if (props.coupon?.display_state === 'owned') {
        return clinicBenefitLabel.value || t('myCoupon.card.visualReady')
      }
      if (props.coupon?.display_state === 'history') {
        return props.coupon?.history_type === 'used'
          ? t('myCoupon.card.historyUsed')
          : t('myCoupon.card.historyExpired')
      }
      return clinicBenefitLabel.value || detailLabel.value || t('myCoupon.card.visualReward')
    })
    const actionLabel = computed(() => {
      if (isLocked.value) {
        return t('myCoupon.card.locked')
      }
      if (isOwnedClinicBenefit.value) {
        return t('myCoupon.card.showQr')
      }
      if (isFreeRedeemable.value) {
        return t('myCoupon.card.claimFree')
      }
      if (isPointsRedeemable.value) {
        return t('myCoupon.card.redeemWithPoints')
      }
      return t('myCoupon.card.useInCart')
    })
    const clinicBenefitLabel = computed(() => {
      if (!isClinicBenefit.value) {
        return ''
      }
      return t('myCoupon.card.clinicBenefit')
    })
    const clinicBenefitCodeLabel = computed(() => {
      const issuedCode = props.coupon?.clinic_benefit_issue?.issued_code
      if (!issuedCode) {
        return ''
      }
      return t('myCoupon.card.clinicBenefitCode', [issuedCode])
    })
    const usageLimitLabel = computed(() => {
      if (props.coupon?.remaining_use_count === null || props.coupon?.remaining_use_count === undefined) {
        return ''
      }
      return t('myCoupon.card.remainingUse', [props.coupon.remaining_use_count])
    })
    // Recent-link coupons: countdown of days left in the user's eligibility window.
    // Backend sends recent_link_days_left (null when N/A, 0 when the window closed).
    const recentLinkDaysLeftLabel = computed(() => {
      const daysLeft = props.coupon?.recent_link_days_left
      if (daysLeft === null || daysLeft === undefined || daysLeft <= 0) {
        return ''
      }
      return t('myCoupon.card.recentLinkDaysLeft', [daysLeft])
    })
    const historyTimestampLabel = computed(() => {
      if (!isHistoryItem.value) {
        return ''
      }
      const historyAt = props.coupon?.history_at || props.coupon?.used_at || props.coupon?.expired_at
      if (!historyAt) {
        return ''
      }
      const formatted = dayjs(historyAt).format('DD/MM/YYYY HH:mm')
      return props.coupon?.history_type === 'used'
        ? t('myCoupon.card.usedOn', [formatted])
        : t('myCoupon.card.expiredOn', [formatted])
    })
    const visualClass = computed(() => ({
      'is-free': isFreeRedeemable.value,
      'is-points': isPointsRedeemable.value,
      'is-clinic': isClinicBenefit.value,
      'is-owned': props.coupon?.display_state === 'owned',
      'is-history': isHistoryItem.value,
      'is-locked': isLocked.value,
      'is-birthday': props.coupon?.is_birthday_month_only,
    }))

    /** method */
    const handleAction = () => {
      if (isLocked.value) {
        return
      }
      emit('action', props.coupon)
    }
    const handleCardClick = () => {
      if (props.mode === 'cart') {
        emit('action', props.coupon)
        return
      }
      if (props.mode === 'browse') {
        emit('detail', props.coupon)
      }
    }

    return {
      t,
      displayName,
      couponImage,
      detailLabel,
      displayExpired,
      isSelected,
      isBrowseMode,
      isCartMode,
      isLocked,
      isRedeemable,
      isFreeRedeemable,
      isPointsRedeemable,
      isOwnedOrPublic,
      isClinicBenefit,
      isOwnedClinicBenefit,
      isHistoryItem,
      showCheck,
      showActionButton,
      badgeLabel,
      badgeClass,
      scopeLabel,
      minLabel,
      stateLabel,
      stateChipClass,
      subtitleLabel,
      clinicBenefitLabel,
      clinicBenefitCodeLabel,
      usageLimitLabel,
      recentLinkDaysLeftLabel,
      historyTimestampLabel,
      visualClass,
      actionLabel,
      handleAction,
      handleCardClick,
    }
  }
}
</script>

<style lang="scss" scoped>

.coupon-card-wrapper {
  display: flex;
  align-items: stretch;
  border: 1px solid #dbe3f2;
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 14px 32px rgba(40, 59, 108, 0.08);
  @media screen and (min-width: 1024px) {
    border-radius: 24px;
  }
  &.is-selected {
    border: 1px solid var(--success-color);
    box-shadow: 0 18px 38px rgba(76, 199, 100, 0.18);
  }
  &.is-locked {
    opacity: 0.72;
    cursor: default;
    background-color: #f6f7fb;
  }
  &.is-history {
    opacity: 0.7;
    background-color: #f5f6fa;
    box-shadow: none;
    .coupon-image {
      filter: grayscale(1);
    }
  }
  &.is-browse {
    padding-right: 16px;
  }
  &.is-cart {
    align-items: stretch;
    min-height: 124px;
    @media screen and (min-width: 1024px) {
      min-height: 132px;
    }
    .visual-wrapper {
      width: 124px;
      height: 124px;
      align-self: stretch;
      @media screen and (min-width: 1024px) {
        width: 132px;
        height: 132px;
      }
    }
    .content-wrapper {
      padding: 12px 12px 12px 16px;
      min-height: 124px;
      justify-content: center;
      @media screen and (min-width: 1024px) {
        padding: 12px 16px 12px 18px;
        min-height: 132px;
      }
    }
  }
  .visual-wrapper {
    position: relative;
    width: 112px;
    height: 112px;
    flex-shrink: 0;
    align-self: center;
    overflow: hidden;
    background: #eef2fb;
    display: flex;
    align-items: center;
    justify-content: center;
    @media screen and (min-width: 1024px) {
      width: 120px;
      height: 120px;
    }
  }
  .coupon-image {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    opacity: 1;
  }
  .visual-overlay {
    position: absolute;
    inset: 0;
    background: transparent;
    pointer-events: none;
  }
  .content-wrapper {
    padding: 12px 12px 12px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
    flex: 1;
    min-height: 112px;
    @media screen and (min-width: 1024px) {
      padding: 12px 14px 12px 16px;
      min-height: 120px;
    }
  }

  .badge {
    border-radius: 999px;
    padding: 5px 9px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #60708a;
    background: #eef2fb;
    &.is-in-store {
      background: rgba(28, 45, 236, 0.1);
      color: #1c2dec;
    }
    &.is-birthday {
      background: rgba(255, 105, 180, 0.12);
      color: #d0488b;
    }
    &.is-points {
      background: rgba(76, 199, 100, 0.12);
      color: #229a43;
    }
  }
  .content-badge {
    flex-shrink: 0;
  }
  .chip-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .content-top {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    position: relative;
  }
  .content-top-cart {
    gap: 0;
  }
  .state-chip {
    border-radius: 999px;
    padding: 4px 9px;
    font-size: 10px;
    font-weight: 700;
    color: #44566c;
    background: #eef2fb;
    &.is-free {
      background: rgba(76, 199, 100, 0.14);
      color: #229a43;
    }
    &.is-points {
      background: rgba(28, 45, 236, 0.1);
      color: #1c2dec;
    }
    &.is-owned,
    &.is-public,
    &.is-clinic {
      background: rgba(95, 113, 137, 0.14);
      color: #44566c;
    }
    &.is-locked {
      background: rgba(161, 75, 75, 0.12);
      color: #a14b4b;
    }
    &.is-history {
      background: rgba(120, 129, 145, 0.14);
      color: #606b7a;
    }
  }
  .header {
    font-size: 17px;
    line-height: 1.2;
    font-weight: 800;
    color: #111a2d;
    word-break: break-word;
    @media screen and (min-width: 1024px) {
      font-size: 18px;
    }
  }
  .header-cart {
    font-size: 18px;
    line-height: 1.24;
    @media screen and (min-width: 1024px) {
      font-size: 20px;
    }
  }
  .subtitle {
    color: #54657e;
    font-size: 12px;
    line-height: 1.35;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    @media screen and (min-width: 1024px) {
      font-size: 13px;
    }
  }
  .meta-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .meta-pill {
    border-radius: 999px;
    padding: 4px 9px;
    font-size: 10px;
    font-weight: 600;
    color: #5c6f88;
    background: #f3f6fc;
    line-height: 1.3;
    &.is-days-left {
      background: rgba(255, 153, 0, 0.14);
      color: #c26a00;
      font-weight: 800;
    }
  }
  .detail {
    color: #66758b;
    font-size: 11px;
    line-height: 1.35;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    @media screen and (min-width: 1024px) {
      font-size: 12px;
      -webkit-line-clamp: 2;
    }
  }
  .lock-reason {
    color: #A14B4B;
    font-size: 11px;
    line-height: 1.35;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .clinic-code {
    color: #44566C;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    word-break: break-word;
  }
  .content-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
  }
  .expired {
    color: #878787;
    font-size: 11px;
    line-height: 1.35;
    word-break: break-word;
    flex: 1;
    @media screen and (min-width: 1024px) {
      font-size: 12px;
    }
  }
  .detail-link {
    border: 0;
    background: transparent;
    color: #1c2dec;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    padding: 0;
  }
  .detail-link-top {
    position: absolute;
    top: 0;
    right: 0;
  }
  &.is-browse {
    .chip-row,
    .header,
    .subtitle,
    .meta-row,
    .detail,
    .lock-reason,
    .clinic-code {
      padding-right: 108px;
    }
  }
}

.check-wrapper {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--success-color);
  margin: auto 12px auto 8px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
  &.is-selected {
    background-color: var(--success-color);
  }
}

.action-btn {
  margin: auto 0 auto 8px;
  min-width: 96px;
  padding: 10px 12px;
  border-radius: 999px;
  border: 0;
  font-size: 12px;
  font-weight: 800;
  color: var(--white-color);
  background-color: var(--secondary-color);
  flex-shrink: 0;
  box-shadow: 0 10px 22px rgba(28, 45, 236, 0.16);
  @media screen and (min-width: 1024px) {
    min-width: 110px;
    padding: 10px 14px;
    font-size: 12px;
  }
  &.secondary {
    background-color: #5F7189;
    box-shadow: none;
  }
  &.free {
    background-color: #4cc764;
    box-shadow: 0 10px 22px rgba(76, 199, 100, 0.2);
  }
  &.locked {
    background-color: #C3C3C3;
    color: #ffffff;
    cursor: not-allowed;
    box-shadow: none;
  }
}
</style>
